'use client'

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/context/AuthContext'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import {
  graphqlRequest,
  PROJECTS_QUERY,
  CREATE_PROJECT_MUTATION,
  PROJECT_MEMBERS_QUERY,
  ADD_PROJECT_MEMBER_MUTATION,
  UPDATE_PROJECT_MEMBER_MUTATION,
  REMOVE_PROJECT_MEMBER_MUTATION,
} from '@/lib/api-client'
import { listSpaces } from '@/features/spaces/api'
import { api } from '@/api/client'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@goodday-clone/ui'
import {
  Plus,
  FolderKanban,
  Layers,
  CheckSquare,
  Search,
  Sparkles,
  Users,
  Zap,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  LayoutGrid,
  List,
  GitBranch,
  FileSpreadsheet,
  Code2,
  Headphones,
  Briefcase,
  ChevronDown,
  X,
  Star,
} from 'lucide-react'
import { ServicesPicker } from '@/components/sales/ServicesPicker'
import {
  groupServicesByCategory,
  getDepartmentsForServices,
} from '@/shared/constants/services'
import { DepartmentsPicker } from '@/features/spaces/components/DepartmentsPicker'
import { chipToneFor } from '@/features/spaces/boardUtils'

// Project type details
function getTypeDetails(type: string) {
  switch (type) {
    case 'service':
      return { label: 'Service Desk', icon: Headphones, badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800' }
    case 'business':
    default:
      return { label: 'Business Project', icon: Briefcase, badgeBg: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800' }
  }
}

// Gradient themes for project avatars
const COLOR_THEMES = [
  { gradient: 'from-blue-600 to-indigo-700', lightBg: 'bg-blue-50 dark:bg-blue-950/40', border: 'border-blue-200/80 dark:border-blue-800/40', text: 'text-blue-700 dark:text-blue-300', ring: 'hover:border-blue-400' },
  { gradient: 'from-emerald-500 to-teal-700', lightBg: 'bg-emerald-50 dark:bg-emerald-950/40', border: 'border-emerald-200/80 dark:border-emerald-800/40', text: 'text-emerald-700 dark:text-emerald-300', ring: 'hover:border-emerald-400' },
  { gradient: 'from-purple-600 to-pink-600', lightBg: 'bg-purple-50 dark:bg-purple-950/40', border: 'border-purple-200/80 dark:border-purple-800/40', text: 'text-purple-700 dark:text-purple-300', ring: 'hover:border-purple-400' },
  { gradient: 'from-amber-500 to-orange-600', lightBg: 'bg-amber-50 dark:bg-amber-950/40', border: 'border-amber-200/80 dark:border-amber-800/40', text: 'text-amber-700 dark:text-amber-300', ring: 'hover:border-amber-400' },
  { gradient: 'from-rose-500 to-red-600', lightBg: 'bg-rose-50 dark:bg-rose-950/40', border: 'border-rose-200/80 dark:border-rose-800/40', text: 'text-rose-700 dark:text-rose-300', ring: 'hover:border-rose-400' },
  { gradient: 'from-cyan-600 to-blue-600', lightBg: 'bg-cyan-50 dark:bg-cyan-950/40', border: 'border-cyan-200/80 dark:border-cyan-800/40', text: 'text-cyan-700 dark:text-cyan-300', ring: 'hover:border-cyan-400' },
]

function getTheme(key: string) {
  let hash = 0
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash)
  const index = Math.abs(hash) % COLOR_THEMES.length
  return COLOR_THEMES[index]
}

// Template options
const TEMPLATES = [
  { id: 'software-kanban', name: 'Software - Kanban', description: 'Kanban board for software teams', type: 'business', icon: FolderKanban, color: 'bg-blue-500', projectKind: 'kanban' },
  { id: 'software-scrum', name: 'Software - Scrum', description: 'Scrum board with sprints', type: 'business', icon: GitBranch, color: 'bg-purple-500', projectKind: 'scrum' },
  { id: 'hr', name: 'HR', description: 'Human resources workflow', type: 'business', icon: Users, color: 'bg-rose-500', projectKind: 'generic' },
  { id: 'marketing', name: 'Marketing', description: 'Marketing campaigns and content', type: 'business', icon: Sparkles, color: 'bg-pink-500', projectKind: 'generic' },
  { id: 'finance', name: 'Finance', description: 'Financial tracking and approvals', type: 'business', icon: Briefcase, color: 'bg-amber-500', projectKind: 'generic' },
  { id: 'design', name: 'Design', description: 'Design projects and reviews', type: 'business', icon: Code2, color: 'bg-indigo-500', projectKind: 'kanban' },
  { id: 'blank', name: 'Blank Project', description: 'Start from scratch', type: 'business', icon: FileSpreadsheet, color: 'bg-slate-500', projectKind: 'generic' },
]

function deriveKey(name: string): string {
  const letters = name.toUpperCase().replace(/[^A-Z]/g, '')
  const key = letters.slice(0, 4) || 'PRJ'
  return key.length < 2 ? `${key}X` : key
}

interface Project {
  id: string
  name: string
  description: string | null
  key: string
  color: string | null
  icon: string | null
  type: string
  projectType: string
  /** Master-data department names; decides which department admins see the project. */
  departments?: string[]
  issueKeyCounter: number
  enabledIssueTypes: string[]
  status: string
  tasksCount: number
  membersCount: number
  isDealWon?: boolean
  sourceDealId?: string | null
  services?: string[]
  createdAt: string
  isStarred?: boolean
  issueStats: {
    total: number
    done: number
    inProgress: number
    todo: number
    progressPercent: number
  }
  members: Array<{
    id: string
    memberId: string
    accessLevel: string
    userId: string | null
    name: string | null
    email: string | null
    avatarUrl: string | null
    role: string | null
    user: {
      id: string
      name: string | null
      email: string | null
      avatarUrl: string | null
    } | null
  }>
}

function ActionTooltip({ content, children }: { content: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      {children}
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  )
}

function Separator({ className = '' }: { className?: string }) {
  return <div className={`h-px bg-border my-2 ${className}`} />
}

interface SpacePageProps {
  searchParams?: { [key: string]: string | string[] | undefined }
}

export default function SpacesPage({ searchParams }: SpacePageProps = {}) {
  const { organization, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const pathname = useLocation().pathname

  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedDept, setSelectedDept] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [starFilter, setStarFilter] = useState<'all' | 'starred' | 'unstarred'>('all')

  // New project form state
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#6366f1')
  const [projectType, setProjectType] = useState('business')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>('software-kanban')
  const [services, setServices] = useState<string[]>([])
  // Departments whose admins can see this project (members always can).
  const [projectDepartments, setProjectDepartments] = useState<string[]>([])

  const loadProjects = useCallback(async () => {
    if (!organization?.id) {
      // Still resolving auth — wait for it to settle rather than declaring
      // "no org" prematurely. Once auth finishes with no organization at
      // all, stop spinning and surface that instead of hanging forever.
      if (!authLoading) {
        setLoading(false)
        setError('No organization found for this account.')
      }
      return
    }
    setLoading(true)
    setError(null)
    try {
      const orgId = organization.id
      try {
        const data = await graphqlRequest<{ projects: Project[] }>(PROJECTS_QUERY, { organizationId: orgId })
        // An empty list is a valid answer (the user has no projects), not a
        // reason to fall back to another endpoint.
        if (data && Array.isArray(data.projects)) {
          setProjects(data.projects)
          setLoading(false)
          return
        }
      } catch (gqlErr) {
        console.warn('GraphQL projects query failed, falling back to REST:', gqlErr)
      }

      // Fallback 1: REST organization projects
      try {
        const orgProjects = await api.get<any>(`/organizations/${orgId}/projects`)
        const pList = Array.isArray(orgProjects) ? orgProjects : (orgProjects?.data ?? [])
        if (pList.length > 0) {
          const mapped: Project[] = pList.map((p: any) => ({
            id: p.id,
            name: p.name,
            description: p.description || null,
            key: p.key,
            color: p.color || '#6366f1',
            icon: null,
            type: p.type || 'business',
            projectType: p.projectType || 'business',
            departments: p.departments ?? [],
            issueKeyCounter: 0,
            enabledIssueTypes: ['task', 'bug', 'story'],
            status: p.status || 'active',
            tasksCount: p.tasksCount || 0,
            membersCount: p.membersCount || 1,
            createdAt: p.createdAt || new Date().toISOString(),
            issueStats: {
              total: p.tasksCount || 0,
              done: 0,
              inProgress: p.tasksCount || 0,
              todo: 0,
              progressPercent: 0,
            },
            members: [],
          }))
          setProjects(mapped)
          setLoading(false)
          return
        }
      } catch (restErr) {
        console.warn('REST organization projects failed:', restErr)
      }

      // Fallback 2: REST listSpaces()
      try {
        const spaces = await listSpaces()
        if (spaces && spaces.length > 0) {
          const mapped: Project[] = spaces.map((s) => ({
            id: s.id,
            name: s.name,
            description: s.description || null,
            key: s.key,
            color: s.color || '#6366f1',
            icon: null,
            type: s.type || 'business',
            projectType: s.type || 'business',
            departments: s.departments ?? [],
            issueKeyCounter: 0,
            enabledIssueTypes: ['task', 'bug', 'story'],
            status: 'active',
            tasksCount: 0,
            membersCount: s.members?.length || 0,
            createdAt: s.createdAt,
            issueStats: {
              total: 0,
              done: 0,
              inProgress: 0,
              todo: 0,
              progressPercent: 0,
            },
            members: (s.members || []).map((m: any) => ({
              id: m.id || m.userId,
              memberId: m.userId || m.id,
              accessLevel: m.role || 'MEMBER',
              userId: m.userId || m.id,
              name: m.name,
              email: m.email,
              avatarUrl: m.avatarUrl || null,
              role: m.role || 'MEMBER',
              user: {
                id: m.userId || m.id,
                name: m.name,
                email: m.email,
                avatarUrl: m.avatarUrl || null,
              },
            })),
          }))
          setProjects(mapped)
          setLoading(false)
          return
        }
      } catch (restErr) {
        console.warn('REST listSpaces failed:', restErr)
      }

      setProjects([])
    } catch (err: any) {
      console.error('Failed to load projects:', err)
      setError(err.message || 'Failed to load projects')
    } finally {
      setLoading(false)
    }
  }, [organization?.id, authLoading])

  // Starred spaces from localStorage
  const [starredIds, setStarredIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    try {
      const starred = JSON.parse(localStorage.getItem('goodday_starred_spaces') || '[]')
      setStarredIds(new Set(Array.isArray(starred) ? starred : []))
    } catch {
      setStarredIds(new Set())
    }
  }, [])

  const toggleStar = useCallback((projectId: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setStarredIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) {
        next.delete(projectId)
        toast.success('Removed from starred')
      } else {
        next.add(projectId)
        toast.success('Space starred')
      }
      try {
        localStorage.setItem('goodday_starred_spaces', JSON.stringify(Array.from(next)))
      } catch {
        // ignore storage errors
      }
      return next
    })
  }, [])

  const isStarred = (projectId: string) => starredIds.has(projectId)

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'goodday_starred_spaces') {
        try {
          const starred = JSON.parse(e.newValue || '[]')
          setStarredIds(new Set(Array.isArray(starred) ? starred : []))
        } catch {
          // ignore
        }
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  useEffect(() => {
    const handleCreated = () => {
      loadProjects()
    }
    window.addEventListener('projectCreated', handleCreated)
    return () => window.removeEventListener('projectCreated', handleCreated)
  }, [loadProjects])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreateError(null)

    if (!name.trim()) {
      setCreateError('Please enter a project name')
      return
    }

    const trimmedKey = (key || deriveKey(name)).trim().toUpperCase()
    if (!trimmedKey) {
      setCreateError('Please enter a project key (e.g. PRJ)')
      return
    }

    if (!organization?.id) {
      setCreateError('Organization not loaded yet — please refresh and try again.')
      return
    }
    const effectiveOrgId = organization.id

    setCreating(true)
    try {
      const tmpl = TEMPLATES.find((t) => t.id === selectedTemplate)
      const projectKind = tmpl?.projectKind || 'generic'
      const enabledTypes = ['software-scrum', 'software-kanban'].includes(tmpl?.id || '')
        ? ['task', 'story', 'bug', 'epic', 'subtask']
        : ['task', 'story', 'subtask']

      let createdProject: any = null
      try {
        const res = await graphqlRequest<{ createProject: Project }>(CREATE_PROJECT_MUTATION, {
          input: {
            name: name.trim(),
            key: trimmedKey,
            description: description.trim() || undefined,
            color,
            icon: 'folder',
            type: projectKind,
            projectType,
            departments: projectDepartments,
            enabledIssueTypes: enabledTypes,
            organizationId: effectiveOrgId,
            services,
          },
        })
        if (res?.createProject) {
          createdProject = res.createProject
        }
      } catch (gqlErr) {
        console.warn('GraphQL createProject failed, attempting REST create:', gqlErr)
      }

      if (!createdProject) {
        const restRes = await api.post<any>(
          `/projects?organizationId=${effectiveOrgId}`,
          {
            name: name.trim(),
            key: trimmedKey,
            description: description.trim() || undefined,
            color,
            type: projectKind,
            services,
            departments: projectDepartments,
          },
          { skipErrorToast: true }
        )
        createdProject = restRes
      }

      if (createdProject) {
        setName('')
        setKey('')
        setDescription('')
        setServices([])
        setProjectDepartments([])
        setCreateError(null)
        setModalOpen(false)
        await loadProjects()
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('projectCreated', { detail: createdProject }))
        }
      }
    } catch (err: any) {
      console.error('Failed to create project:', err)
      toast.error(getApiErrorMessage(err, 'create projects'))
    } finally {
      setCreating(false)
    }
  }

  // Filter projects
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchesSearch =
        !search.trim() ||
        project.name.toLowerCase().includes(search.toLowerCase()) ||
        project.key.toLowerCase().includes(search.toLowerCase()) ||
        (project.departments ?? []).some((d) => d.toLowerCase().includes(search.toLowerCase()))

      const matchesDept = selectedDept === 'all' || (project.departments ?? []).includes(selectedDept)

      const matchesStar = starFilter === 'all' ||
        (starFilter === 'starred' && isStarred(project.id)) ||
        (starFilter === 'unstarred' && !isStarred(project.id))

      return matchesSearch && matchesDept && matchesStar
    })
  }, [projects, search, selectedDept, starFilter, isStarred])

  // Summary Metrics
  const totalProjects = projects.length
  const totalInProgress = useMemo(() => projects.reduce((acc, p) => acc + (p.issueStats?.inProgress || 0), 0), [projects])
  const totalDone = useMemo(() => projects.reduce((acc, p) => acc + (p.issueStats?.done || 0), 0), [projects])
  const totalIssues = useMemo(() => projects.reduce((acc, p) => acc + (p.issueStats?.total || 0), 0), [projects])
  const overallRate = totalIssues > 0 ? Math.round((totalDone / totalIssues) * 100) : 0

  // Department filter pills: the departments projects actually belong to (they come from master data).
  const deptCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    projects.forEach((p) => {
      for (const dept of p.departments ?? []) counts[dept] = (counts[dept] || 0) + 1
    })
    return counts
  }, [projects])
  const departmentPills = useMemo(() => {
    const pills = Object.entries(deptCounts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    // Keep the selected department visible even if the current list has none left.
    if (selectedDept !== 'all' && !(selectedDept in deptCounts)) pills.push([selectedDept, 0])
    return pills
  }, [deptCounts, selectedDept])

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <div className="mx-auto max-w-7xl py-24">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <div className="mx-auto max-w-7xl py-24 text-center">
          <p className="text-red-600 dark:text-red-400">Error: {error}</p>
          <Button onClick={loadProjects} className="mt-4">Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full min-h-full flex flex-col">
      <div className="w-full space-y-6">
        {/* Header Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-tr from-purple-600 via-indigo-600 to-blue-500 text-white shadow-lg shadow-primary/25">
              <FolderKanban className="h-6 w-6" />
            </span>
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-2xl font-bold text-foreground tracking-tight">Projects</h1>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                  {totalProjects} {totalProjects === 1 ? 'Workspace' : 'Workspaces'}
                </span>
              </div>
              <p className="mt-0.5 text-xs sm:text-sm text-muted-foreground">
                Manage work boards, sprints, backlogs, and cross-functional team delivery.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 via-primary to-indigo-600 px-4 py-2 text-xs sm:text-sm font-semibold text-white shadow-md shadow-primary/25 transition-all hover:shadow-lg hover:shadow-primary/35 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Plus className="h-4 w-4 stroke-[2.5]" />
              <span>New Project</span>
            </Button>
          </div>
        </div>

        {/* Quick Analytics Metrics Banner */}
        <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-4">
          <Card className="backdrop-blur-sm transition-all hover:border-primary/30" interactive>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                    <Layers className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Total Projects</div>
                    <div className="text-xl font-extrabold text-foreground tracking-tight">{totalProjects}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm transition-all hover:border-amber-500/30" interactive>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">In Progress</div>
                    <div className="text-xl font-extrabold text-foreground tracking-tight">
                      {totalInProgress} <span className="text-xs font-medium text-muted-foreground">tasks</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm transition-all hover:border-emerald-500/30" interactive>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Completed</div>
                    <div className="text-xl font-extrabold text-foreground tracking-tight">
                      {totalDone} <span className="text-xs font-medium text-muted-foreground">tasks</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm transition-all hover:border-purple-500/30" interactive>
            <CardContent className="p-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">Overall Progress</div>
                      <div className="text-xl font-extrabold text-foreground tracking-tight">
                        {overallRate}% <span className="text-xs font-medium text-muted-foreground">rate</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.max(0, overallRate))}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Department Filters Toolbar */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-1 flex-wrap items-center gap-2.5">
                {/* Search Box */}
                <div className="relative min-w-[220px] max-w-sm flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search projects by name, key, or department..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-8.5 w-full rounded-lg border border-border bg-muted/50 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus:border-primary focus:bg-background focus:outline-hidden focus:ring-1 focus:ring-primary"
                  />
                </div>

                {/* Starred Filter */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setStarFilter('all')}
                    className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors shrink-0 ${starFilter === 'all'
                      ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setStarFilter('starred')}
                    className={`flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors shrink-0 ${starFilter === 'starred'
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-semibold shadow-xs'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                  >
                    <Star className={`h-3 w-3 ${starFilter === 'starred' ? 'fill-amber-400 text-amber-500' : ''}`} />
                    Starred
                  </button>
                  <button
                    onClick={() => setStarFilter('unstarred')}
                    className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors shrink-0 ${starFilter === 'unstarred'
                      ? 'bg-muted-foreground/20 text-foreground font-semibold shadow-xs'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                  >
                    Unstarred
                  </button>
                </div>

                {/* Department Filter Pills */}
                <div className="flex items-center gap-1 overflow-x-auto py-0.5 scrollbar-hide">
                  <button
                    onClick={() => setSelectedDept('all')}
                    className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors shrink-0 ${selectedDept === 'all'
                      ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                  >
                    All
                  </button>
                  {departmentPills.map(([dept, count]) => (
                    <button
                      key={dept}
                      onClick={() => setSelectedDept(dept)}
                      className={`flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors shrink-0 ${selectedDept === dept
                        ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                    >
                      <span>{dept}</span>
                      <span className={`rounded-full px-1 text-[10px] ${selectedDept === dept ? 'bg-primary/30 text-primary-foreground' : 'bg-muted/50 text-muted-foreground'}`}>
                        {count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center rounded-lg border border-border bg-muted p-0.5">
                <TooltipProvider>
                  <ActionTooltip content="Grid View">
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`rounded-md p-1.5 transition-colors ${viewMode === 'grid' ? 'bg-background text-primary shadow-xs' : 'text-muted-foreground hover:text-foreground'
                          }`}
                      >
                        <LayoutGrid className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                  </ActionTooltip>
                  <ActionTooltip content="List View">
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`rounded-md p-1.5 transition-colors ${viewMode === 'list' ? 'bg-background text-primary shadow-xs' : 'text-muted-foreground hover:text-foreground'
                          }`}
                      >
                        <List className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                  </ActionTooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Section */}
        {filteredProjects.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 px-4">
              <FolderKanban className="h-10 w-10 text-primary mx-auto mb-3 opacity-40" />
              <h3 className="font-semibold text-foreground">No projects found</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4">
                {search ? `No projects match "${search}". Try adjusting your filters.` : 'Get started by creating your first team project.'}
              </p>
              <Button onClick={() => setModalOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Plus className="h-4 w-4 mr-1" /> Create Project
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          /* Grid View of Rich Jira-style Cards */
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {filteredProjects.map((project) => {
              const theme = getTheme(project.key)
              const typeInfo = getTypeDetails(project.projectType)
              const TypeIcon = typeInfo.icon
              const stats = project.issueStats || { total: 0, done: 0, inProgress: 0, todo: 0, progressPercent: 0 }
              const members = project.members || []

              return (
                <Card
                  key={project.id}
                  interactive
                  className={`group relative flex flex-col justify-between p-5 transition-all duration-200 hover:-translate-y-1 ${theme.ring}`}
                  onClick={() => navigate(`/spaces/${project.id}`)}
                >
                  <div>
                    {/* Card Top Row: Gradient Avatar & Badges */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-md font-bold text-sm tracking-wider ${theme.gradient}`}
                        >
                          {project.key.slice(0, 3)}
                        </div>
                        <div>
                          <span className="text-[11px] font-bold text-muted-foreground tracking-wide uppercase">{project.key}</span>
                          <h2 className="cursor-pointer text-base font-bold text-foreground leading-tight transition-colors hover:text-primary line-clamp-1">
                            {project.name}
                          </h2>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Star Toggle */}
                        <button
                          onClick={(e) => toggleStar(project.id, e)}
                          className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${isStarred(project.id)
                              ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/40'
                              : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:text-slate-500 dark:hover:text-amber-400 dark:hover:bg-amber-950/40'
                            }`}
                          title={isStarred(project.id) ? 'Remove from starred' : 'Star this space'}
                        >
                          <Star
                            className={`h-3.5 w-3.5 ${isStarred(project.id) ? 'fill-amber-400 text-amber-500 dark:fill-amber-500 dark:text-amber-400' : ''}`}
                          />
                        </button>

                        {/* Open Project Arrow */}
                        <TooltipProvider>
                          <ActionTooltip content="Open Project">
                            <TooltipTrigger asChild>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/spaces/${project.id}`)
                                }}
                                className="rounded-lg p-1.5 text-muted-foreground opacity-0 transition-all hover:bg-muted hover:text-foreground group-hover:opacity-100"
                              >
                                <ArrowUpRight className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                          </ActionTooltip>
                        </TooltipProvider>
                      </div>
                    </div>

                    {/* Department & Project Type Badges */}
                    <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
                      {(project.departments ?? []).map((dept) => (
                        <span
                          key={dept}
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${chipToneFor(dept)}`}
                        >
                          {dept}
                        </span>
                      ))}

                      <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium ${typeInfo.badgeBg}`}>
                        <TypeIcon className="h-3 w-3" />
                        <span>{typeInfo.label}</span>
                      </span>

                      {project.isDealWon && (
                        <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                          <Zap className="h-3 w-3" />
                          <span>Deal-Won</span>
                        </span>
                      )}
                    </div>

                    {/* Description if present */}
                    {project.description && (
                      <p className="mt-2.5 text-xs text-muted-foreground line-clamp-2">
                        {project.description}
                      </p>
                    )}

                    {/* Categorized Services */}
                    {project.services && project.services.length > 0 && (
                      <div className="mt-3 space-y-1.5 pt-2.5 border-t border-border/50">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                          <Layers className="h-3 w-3 text-primary" />
                          <span>Services & Categories</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {groupServicesByCategory(project.services).map(({ category, services: svcs }) => (
                            <span
                              key={category.category}
                              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium border ${category.badgeClass}`}
                              title={`${category.category}: ${svcs.join(', ')}`}
                            >
                              <span className="font-bold">{category.departmentLabel}:</span>
                              <span>{svcs.slice(0, 2).join(', ')}{svcs.length > 2 ? ` +${svcs.length - 2}` : ''}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Issues Progress Section */}
                    <div className="mt-4 rounded-xl bg-muted/50 p-3 border border-border">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-foreground">Delivery Status</span>
                        <span className="font-bold text-foreground">
                          {stats.total > 0 ? `${stats.progressPercent}%` : '0 issues'}
                        </span>
                      </div>

                      {/* Multi-segment Progress Bar */}
                      <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-muted">
                        {stats.total > 0 ? (
                          <>
                            <div
                              style={{ width: `${(stats.done / stats.total) * 100}%` }}
                              className="bg-emerald-500 transition-all duration-300"
                              title={`${stats.done} Done`}
                            />
                            <div
                              style={{ width: `${(stats.inProgress / stats.total) * 100}%` }}
                              className="bg-blue-500 transition-all duration-300"
                              title={`${stats.inProgress} In Progress`}
                            />
                            <div
                              style={{ width: `${(stats.todo / stats.total) * 100}%` }}
                              className="bg-muted-foreground/30 transition-all duration-300"
                              title={`${stats.todo} To Do`}
                            />
                          </>
                        ) : (
                          <div className="w-full bg-muted" />
                        )}
                      </div>

                      {/* Stats numbers breakdown */}
                      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          <span>{stats.done} Done</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full bg-blue-500" />
                          <span>{stats.inProgress} Active</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full bg-muted-foreground" />
                          <span>{stats.todo} Todo</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Bottom Row: Team Avatars & Quick Navigation Links */}
                  <div className="mt-4 flex items-center justify-between border-t border-border pt-3.5">
                    {/* Team Members Avatar Stack */}
                    <div className="flex items-center">
                      {members.length > 0 ? (
                        <div className="flex items-center -space-x-1.5">
                          {members.slice(0, 3).map((m) => (
                            <TooltipProvider key={m.id}>
                              <ActionTooltip content={`${m.name || m.user?.name || 'Unknown'} (${m.accessLevel || m.role || 'member'})`}>
                                <TooltipTrigger asChild>
                                  <div className="relative rounded-full ring-2 ring-background">
                                    <div
                                      className="h-6 w-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold"
                                      style={{ backgroundColor: m.user?.avatarUrl ? 'transparent' : undefined }}
                                    >
                                      {m.user?.avatarUrl ? (
                                        <img src={m.user.avatarUrl} alt={m.name || m.user?.name || ''} className="h-6 w-6 rounded-full" />
                                      ) : (
                                        (m.name || m.user?.name || 'U').slice(0, 2).toUpperCase()
                                      )}
                                    </div>
                                  </div>
                                </TooltipTrigger>
                              </ActionTooltip>
                            </TooltipProvider>
                          ))}
                          {project.membersCount && project.membersCount > 3 && (
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground ring-2 ring-background">
                              +{project.membersCount - 3}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Users className="h-3.5 w-3.5" />
                          <span>1 Member</span>
                        </div>
                      )}
                    </div>

                    {/* Quick navigation action shortcuts */}
                    <div className="flex items-center gap-1">
                      <TooltipProvider>
                        <ActionTooltip content="Open Board">
                          <TooltipTrigger asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/spaces/${project.id}/board`)
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                              <FolderKanban className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                        </ActionTooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <ActionTooltip content="Open Backlog">
                          <TooltipTrigger asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/spaces/${project.id}/backlog`)
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                              <Layers className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                        </ActionTooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <ActionTooltip content="Open Timeline">
                          <TooltipTrigger asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/spaces/${project.id}/timeline`)
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                              <GitBranch className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                        </ActionTooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <ActionTooltip content="Open List">
                          <TooltipTrigger asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/spaces/${project.id}/list`)
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                              <FileSpreadsheet className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                        </ActionTooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        ) : (
          /* List View Table */
          <Card className="overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-muted/50 text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 font-semibold">Project</th>
                  <th className="px-4 py-3 font-semibold">Key</th>
                  <th className="px-4 py-3 font-semibold">Department</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Progress</th>
                  <th className="px-4 py-3 font-semibold">Team</th>
                  <th className="px-4 py-3 text-right font-semibold">Starred</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredProjects.map((project) => {
                  const theme = getTheme(project.key)
                  const typeInfo = getTypeDetails(project.projectType)
                  const stats = project.issueStats || { total: 0, done: 0, inProgress: 0, todo: 0, progressPercent: 0 }
                  const members = project.members || []

                  return (
                    <tr
                      key={project.id}
                      onClick={() => navigate(`/spaces/${project.id}`)}
                      className="cursor-pointer transition-colors hover:bg-primary/5"
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white font-bold text-xs ${theme.gradient}`}>
                            {project.key.slice(0, 2)}
                          </div>
                          <div>
                            <div className="font-semibold text-foreground">{project.name}</div>
                            {project.description && <div className="text-muted-foreground line-clamp-1">{project.description}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 font-bold text-muted-foreground">{project.key}</td>
                      <td className="px-4 py-3.5">
                        <div className="space-y-1">
                          <div className="flex flex-wrap gap-1">
                            {(project.departments ?? []).map((dept) => (
                              <span
                                key={dept}
                                className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${chipToneFor(dept)}`}
                              >
                                {dept}
                              </span>
                            ))}
                          </div>
                          {project.services && project.services.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {groupServicesByCategory(project.services).map(({ category, services: svcs }) => (
                                <span
                                  key={category.category}
                                  className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium border ${category.badgeClass}`}
                                  title={`${category.category}: ${svcs.join(', ')}`}
                                >
                                  {svcs.slice(0, 2).join(', ')}{svcs.length > 2 ? ` +${svcs.length - 2}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`rounded px-2 py-0.5 text-[11px] font-medium border ${typeInfo.badgeBg}`}>
                            {typeInfo.label}
                          </span>
                          {project.isDealWon && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                              <Zap className="h-2.5 w-2.5" />
                              <span>Deal-Won</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                            <div style={{ width: `${stats.progressPercent}%` }} className="h-full bg-primary" />
                          </div>
                          <span className="font-semibold text-foreground">{stats.progressPercent}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center -space-x-1">
                          {members.length > 0 ? (
                            members.slice(0, 3).map((m) => (
                              <TooltipProvider key={m.id}>
                                <ActionTooltip content={`${m.name || m.user?.name || 'Unknown'} (${m.accessLevel || m.role || 'member'})`}>
                                  <TooltipTrigger asChild>
                                    <div className="h-6 w-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold ring-2 ring-background">
                                      {m.user?.avatarUrl ? (
                                        <img src={m.user.avatarUrl} alt={m.name || m.user?.name || ''} className="h-6 w-6 rounded-full" />
                                      ) : (
                                        (m.name || m.user?.name || 'U').slice(0, 2).toUpperCase()
                                      )}
                                    </div>
                                  </TooltipTrigger>
                                </ActionTooltip>
                              </TooltipProvider>
                            ))
                          ) : (
                            <span className="text-muted-foreground">1</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleStar(project.id)
                          }}
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${isStarred(project.id)
                              ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/40'
                              : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:text-slate-500 dark:hover:text-amber-400 dark:hover:bg-amber-950/40'
                            }`}
                          title={isStarred(project.id) ? 'Remove from starred' : 'Star this space'}
                        >
                          <Star
                            className={`h-3.5 w-3.5 ${isStarred(project.id) ? 'fill-amber-400 text-amber-500 dark:fill-amber-500 dark:text-amber-400' : ''}`}
                          />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Card>
        )}

        {/* New Project Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl p-6">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">New Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              {createError && (
                <div className="p-3 text-xs text-red-600 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-200 dark:border-red-900">
                  {createError}
                </div>
              )}

              {/* Template Gallery */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Template</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => {
                        setSelectedTemplate(template.id)
                        setProjectType(template.type)
                      }}
                      className={`relative p-3 rounded-lg border-2 transition-all text-left ${selectedTemplate === template.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${template.color}`}>
                          <template.icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground truncate">{template.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{template.description}</div>
                        </div>
                        {selectedTemplate === template.id && (
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                            <CheckSquare className="h-3 w-3" />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-1">
                <Label htmlFor="projName">Project name</Label>
                <Input
                  id="projName"
                  placeholder="e.g. Website Revamp"
                  required
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (!key || key === deriveKey(name)) {
                      setKey(deriveKey(e.target.value))
                    }
                  }}
                  autoFocus
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="projKey">Project Key (Short Code)</Label>
                <Input
                  id="projKey"
                  placeholder="PRJ"
                  required
                  maxLength={10}
                  value={key}
                  onChange={(e) => setKey(e.target.value.toUpperCase())}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="projDesc">Description (Optional)</Label>
                <Textarea
                  id="projDesc"
                  placeholder="Describe project objectives and scope..."
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="projType">Project Type</Label>
                  <Select value={projectType} onValueChange={setProjectType}>
                    <SelectTrigger id="projType">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

              </div>

              <div className="space-y-1">
                <Label htmlFor="projColor">Color Badge</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    id="projColor"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="h-9 w-12 rounded cursor-pointer border border-border"
                  />
                  <span className="text-xs font-mono text-muted-foreground">{color}</span>
                </div>
              </div>

              <div className="space-y-1">
                <Label>Services</Label>
                <ServicesPicker
                  selectedServices={services}
                  onChange={(newSvcs) => {
                    setServices(newSvcs)
                    // Suggest the departments that deliver these services.
                    setProjectDepartments((prev) =>
                      Array.from(new Set([...prev, ...getDepartmentsForServices(newSvcs)]))
                    )
                  }}
                />
              </div>

              <div className="space-y-1">
                <Label>Visible to departments</Label>
                <DepartmentsPicker value={projectDepartments} onChange={setProjectDepartments} />
                <p className="text-xs text-muted-foreground">
                  Admins of these departments can see this project. Assigned members always can.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" loading={creating}>
                  Create Project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}